import jstz from 'jstz'
import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import styled from 'styled-components'
import { Panel } from 'primereact/panel'
import { Button } from 'primereact/button'

import { isAdminDomain } from '../../../../lib/currentDomain'
import { getGreenChargingInterval } from '../../../../lib/get-green-charging-interval'
import { GreenChargingBadge } from '../../../../lib/green-charging-badge'
import TrashIcon from '../../../../lib/icons/trash-icon/trash-icon'
import { Table, TableBody, TableBodyCell, TableHeader, TableHeaderCell, TableRow } from '../../../../lib/table'
import Text from '../../../../ui/Text'
import { SEditSaveButton } from '../../../components/ComponentTypeLabel'
import DropdownMenu, { DropdownMenuItem } from '../../../components/DropdownMenu'
import EmptyListRefresh from '../../../components/EmptyListRefresh'
import InlineAlert, { AlertType } from '../../../components/InlineAlert'
import Input from '../../../components/Input'
import { CheckMarkIcon } from '../../../icons/checkmark-icon'
import { CloseIcon } from '../../../icons/close-icon/CloseIcon'
import { EditIcon } from '../../../icons/edit-icon'
import { RefreshIcon } from '../../../icons/refresh-icon'
import {
    GreenOnlyInterval,
    UpdateGreenChargingGroup,
    useGreenChargingGetLoadBalancerGroupQuery,
    useGreenChargingUpdateLoadBalancerGroupMutation,
    CurrentLimitSchemeItem,
    LoadBalancerGroup,
    useLoadBalancerGroupGetCurrentLimitSchemeQuery,
    useLoadBalancerGroupInsertOrUpdateCurrentLimitSchemeMutation,
} from '../../../redux/api/web-api'
import { notifyError, notifySuccess } from '../../notificationsManager/Notify'
import useText from '../../../hooks/useText'
import Toggle from '../../../components/Toggle'
import { theme } from '../../../../lib/theme'
import { confirm } from '../../../uikit/ConfirmDialog'

export const SIcons = styled.div`
    display: flex;
    gap: 10px;
`

const timeDataInitialState = {
    startHours: undefined,
    startMinutes: undefined,
    current: undefined,
}
export type LoadbalancerCurrentLimitWidgetProps = {
    group: LoadBalancerGroup
    refetchGroup: () => void
}

export const LoadbalancerCurrentLimitWidget = React.memo(function LoadbalancerCurrentLimitWidget({
    group,
    refetchGroup,
}: LoadbalancerCurrentLimitWidgetProps): React.ReactElement {
    const text = useText()
    const groupId = group && group.id
    const [editableRow, setEditableRow] = useState<number | null>(null)
    const [greenChargingLoading, setGreenChargingLoading] = useState(false)
    const [openTimePicker, setOpenTimePicker] = useState(false)
    const [active, setActive] = useState<boolean>(false)
    const [currentItems, setCurrentItems] = useState<Array<CurrentLimitSchemeItem>>([])
    const isFullDayOfCharging = (greenOnlyInterval: GreenOnlyInterval) => {
        const { startHour, startMinute, endHour, endMinute } = greenOnlyInterval
        return startHour === endHour && startMinute === endMinute
    }

    const [updateActivationCurrentLimit] = useLoadBalancerGroupInsertOrUpdateCurrentLimitSchemeMutation()
    const [updateGreenCharging] = useGreenChargingUpdateLoadBalancerGroupMutation()
    const [updateCurrentLimitScheme] = useLoadBalancerGroupInsertOrUpdateCurrentLimitSchemeMutation()
    const {
        data: currentLimitScheme,
        isFetching,
        isLoading,
    } = useLoadBalancerGroupGetCurrentLimitSchemeQuery(
        { id: groupId, currentLimitSchemeId: group.currentLimitSchemeId },
        { skip: !groupId || group?.currentLimitSchemeId === null }
    )

    const { data: greenChargingStatus, refetch: refetchGreenChargingStatus } =
        useGreenChargingGetLoadBalancerGroupQuery(
            {
                loadBalancerGroupId: groupId,
            },
            {
                skip: !groupId,
                selectFromResult: (result) => ({
                    ...result,
                    // when query has error or is fetching, it returns previous data, so we need to return undefined
                    data: result.isError || result.isFetching ? undefined : result.data,
                }),
            }
        )

    useEffect(() => {
        if (!group) return
        // refetch green charging status when group changes
        refetchGreenChargingStatus()
    }, [group, refetchGreenChargingStatus])

    const toggleActivateCurrentScheme = async () => {
        setActive((active) => !active)

        const tempCurrentLimit = {
            ...currentLimitScheme,
            active: !active,
            currentLimitSchemeItems: currentLimitScheme?.currentLimitSchemeItems ?? [],
        }

        if (groupId) {
            try {
                await updateActivationCurrentLimit({
                    id: groupId,
                    currentLimitScheme: tempCurrentLimit,
                }).unwrap()
                const message = active
                    ? text('msgUpdateCurrentLimitDeactivated')
                    : text('msgUpdateCurrentLimitActivated')
                notifySuccess(message)
            } catch (error) {
                const message = active
                    ? text('msgUpdateCurrentLimitDeactivatedError')
                    : text('msgUpdateCurrentLimitActivatedError')
                notifyError(message)
            }
        }
    }

    const turnGreenChargingOff = async () => {
        try {
            if (greenChargingStatus?.id && groupId) {
                setGreenChargingLoading(true)
                const updateGreenChargingGroup: UpdateGreenChargingGroup = {
                    id: greenChargingStatus?.id,
                    greenChargingOn: false,
                }
                await updateGreenCharging({ updateGreenChargingGroup: updateGreenChargingGroup }).unwrap()
                const greenChargingLimitScheme = currentLimitScheme?.currentLimitSchemeItems
                await updateCurrentLimitScheme({
                    id: groupId,
                    currentLimitScheme: {
                        id: group.currentLimitSchemeId ?? 0,
                        currentLimitSchemeItems: greenChargingLimitScheme ?? [],
                        timeZone: jstz.determine().name(),
                        active: false,
                    },
                }).unwrap()
            }
        } catch (exception) {
            notifyError(text('lblGreenChargingFailedError'))
        }
        setGreenChargingLoading(false)
    }

    const [timeData, setTimeData] = useState<CurrentLimitSchemeItem>(timeDataInitialState)

    const {
        register,
        reset,
        handleSubmit,
        formState: { isSubmitting },
    } = useForm<CurrentLimitSchemeItem>({
        defaultValues: {},
    })

    const addCurrent = () => {
        setCurrentItems((items) => [...items, timeData])
    }

    useEffect(() => {
        setCurrentItems(currentLimitScheme?.currentLimitSchemeItems ?? [])
    }, [currentLimitScheme?.currentLimitSchemeItems])

    useEffect(() => {
        setActive(currentLimitScheme?.active ?? false)
    }, [currentLimitScheme?.active])

    const handleRemoveCurrent = async (schemeId?: number) => {
        if (!groupId) return

        const confirmed = await confirm({
            header: text('lblProceedConfirmation'),
            message: text('lblConfirmRemoveCurrentLimitScheme'),
            variant: 'danger',
        })
        if (!confirmed) return

        const temp = currentItems.filter((item) => item?.id !== schemeId)

        const tempCurrentLimit = {
            active: currentLimitScheme?.active,
            timeZone: jstz.determine().name(),
            currentLimitSchemeItems: temp,
        }

        if (groupId) {
            try {
                await updateActivationCurrentLimit({
                    id: groupId ?? 0,
                    currentLimitScheme: tempCurrentLimit,
                }).unwrap()

                notifySuccess(text('msgUpdateCurrentLimitRemoveSuccess'))
                setOpenTimePicker(false)
                reset()
            } catch (error) {
                notifyError(text('msgUpdateCurrentLimitRemoveError'))
            }
        }
    }

    const refreshScheme = () => {
        refetchGroup()
        refetchGreenChargingStatus()
    }

    const loading = isFetching && isLoading

    const showScheduleButton =
        !isAdminDomain &&
        currentLimitScheme?.currentLimitSchemeItems &&
        currentLimitScheme?.currentLimitSchemeItems?.length > 0

    const showEmptyList =
        !loading &&
        !greenChargingStatus?.greenChargingOn &&
        !openTimePicker &&
        (!currentLimitScheme || currentLimitScheme?.currentLimitSchemeItems?.length === 0)

    const showTablelist =
        !loading &&
        ((currentLimitScheme?.currentLimitSchemeItems && currentLimitScheme?.currentLimitSchemeItems?.length > 0) ||
            openTimePicker ||
            (greenChargingStatus?.greenChargingOn && greenChargingStatus?.greenOnlyInterval))

    const showGreenInterval = greenChargingStatus?.greenChargingOn && group?.active

    const disabledScheduleButton =
        group.active || greenChargingStatus?.greenChargingOn || openTimePicker || isFetching || !!editableRow

    const handleEditToggle = (id: number | null) => {
        setEditableRow(id)
    }

    const doEditCurrentLimitScheme = async (schemeId: number) => {
        const updatedItems = currentItems.filter((item) => item?.id !== schemeId)
        updatedItems.push(timeData)

        const tempCurrentLimit = {
            active: currentLimitScheme?.active,
            timeZone: jstz.determine().name(),
            currentLimitSchemeItems: updatedItems,
        }

        if (groupId) {
            try {
                await updateActivationCurrentLimit({
                    id: groupId,
                    currentLimitScheme: tempCurrentLimit,
                }).unwrap()

                notifySuccess(text('msgUpdateCurrentLimitEditSuccess'))
                setEditableRow(null)
                setOpenTimePicker(false)
                setTimeData(timeDataInitialState)
            } catch (error) {
                notifyError(text('msgUpdateCurrentLimitEditError'))
            }
        }
    }

    const doAddCurrentLimitScheme = async () => {
        const tempCurrentLimit = {
            active: currentLimitScheme?.active,
            timeZone: jstz.determine().name(),
            currentLimitSchemeItems: currentItems,
        }
        setEditableRow(null)
        if (groupId) {
            try {
                await updateActivationCurrentLimit({
                    id: groupId,
                    currentLimitScheme: tempCurrentLimit,
                }).unwrap()

                notifySuccess(text('msgUpdateCurrentLimitAddSuccess'))
                setOpenTimePicker(false)
                reset()
            } catch (error) {
                notifyError(text('msgUpdateCurrentLimitAddError'))
            }
        }
    }

    const sortedCurrentItems = currentItems
        ? [...currentItems].sort((a, b) => Number(a.startHours) - Number(b.startHours))
        : []

    return (
        <Panel
            data-testid="current-limit-widget"
            header={
                <div className="flex align-items-center">
                    {text('lblCurrentLimit')}

                    <i
                        className="pi pi-info-circle text-blue-500 ml-2"
                        title={text('lblScheduleCurrentLimitInfoText')}
                        data-testid="info-currentlimit-text"
                    ></i>
                </div>
            }
            icons={
                !isAdminDomain ? (
                    <SIcons key={group?.id}>
                        <Toggle
                            data-testid="lbgroup-toggle"
                            size="md"
                            isToggled={active}
                            toggleFn={() => toggleActivateCurrentScheme()}
                            title={(() => {
                                if (group?.active) {
                                    return text('msgLbActive')
                                } else if (greenChargingStatus?.greenChargingOn && group.active) {
                                    return text('msgChargerIsInGreenChargingMode')
                                } else {
                                    return text('lblActivateCurrentLimit')
                                }
                            })()}
                            disabled={
                                (greenChargingStatus?.greenChargingOn && group.active) ||
                                currentItems?.length == 0 ||
                                group.active
                            }
                        ></Toggle>
                    </SIcons>
                ) : null
            }
        >
            {greenChargingStatus?.greenChargingOn && group.active && (
                <GreenChargingBadge
                    data-testid="green-charging-badge"
                    infoIconTooltip={text('msgGreenChargingIsEnabledInMobileApp')}
                    onTurnOff={turnGreenChargingOff}
                    isTurnOffRequestLoading={greenChargingLoading}
                    turnOffDisabled={greenChargingLoading || !greenChargingStatus?.controlEnabled}
                ></GreenChargingBadge>
            )}
            {currentLimitScheme?.active && isAdminDomain && (
                <InlineAlert
                    data-testid="current-limit-active-alert"
                    text={text('msgcurrentlimitActive')}
                    alertType={AlertType.Info}
                />
            )}

            {showEmptyList && (
                <EmptyListRefresh
                    emptyText={text('msgNoSchedulesCurrentLimit')}
                    action={isAdminDomain ? refreshScheme : () => setOpenTimePicker(true)}
                    icon={isAdminDomain ? <RefreshIcon /> : <i className="pi pi-plus" aria-hidden="true" />}
                    label={isAdminDomain ? text('btnRefresh') : text('btnAddLoadBalancerScheduleSlot')}
                />
            )}
            {showTablelist && (
                <form onSubmit={handleSubmit(doAddCurrentLimitScheme)} noValidate>
                    <Table data-testid="current-limit-list">
                        <TableHeader>
                            <TableRow>
                                <TableHeaderCell data-testid="hour" className="w-4">
                                    <Text code="lblStartTime" />
                                </TableHeaderCell>

                                <TableHeaderCell data-testid="current" className="w-4">
                                    <Text code="lblCurrent" />
                                </TableHeaderCell>
                                <TableHeaderCell></TableHeaderCell>
                            </TableRow>
                        </TableHeader>

                        <TableBody>
                            {(!greenChargingStatus?.greenChargingOn || !group.active) &&
                                sortedCurrentItems?.map((item, index) => (
                                    <TableRow key={index} data-testid={`row-${currentLimitScheme?.id}`}>
                                        {editableRow === item?.id ? (
                                            <TableBodyCell data-testid="current-limit-start">
                                                <Input
                                                    defaultValue={(() => {
                                                        const customDate = new Date()
                                                        customDate.setHours(Number(item.startHours))
                                                        customDate.setMinutes(Number(item.startMinutes))

                                                        return customDate.toTimeString().substring(0, 5)
                                                    })()}
                                                    id="time"
                                                    type="time"
                                                    onChange={(event) => {
                                                        const time = event.target.value

                                                        const [hours, minutes] = time && time.split(':')

                                                        setTimeData({
                                                            ...item,
                                                            startMinutes: Number(minutes),
                                                            startHours: Number(hours),
                                                        })
                                                    }}
                                                    data-testid="addTime"
                                                    min="00:00"
                                                    max="23:59"
                                                />
                                            </TableBodyCell>
                                        ) : (
                                            <TableBodyCell data-testid={`hour-${currentLimitScheme?.id}`}>
                                                {item?.startHours?.toString().padStart(2, '0')}:
                                                {item?.startMinutes?.toString().padStart(2, '0')}
                                            </TableBodyCell>
                                        )}

                                        {editableRow === item?.id ? (
                                            <TableBodyCell>
                                                <Input
                                                    id="current"
                                                    unit="A"
                                                    onChange={(event) => {
                                                        setTimeData({
                                                            ...item,
                                                            current: event.target.value
                                                                ? Number(event.target.value)
                                                                : undefined,
                                                        })
                                                    }}
                                                    type="number"
                                                    data-testid="input-current"
                                                    min="0"
                                                    defaultValue={item?.current}
                                                ></Input>
                                            </TableBodyCell>
                                        ) : (
                                            <TableBodyCell data-testid={`current-${currentLimitScheme?.id}`}>
                                                {item?.current} A
                                            </TableBodyCell>
                                        )}

                                        <TableBodyCell data-testid={`remove-${currentLimitScheme?.id}`}>
                                            {!isAdminDomain &&
                                                (editableRow === item?.id ? (
                                                    <span style={{ float: 'right' }} className="flex gap-2 mt-3">
                                                        <SEditSaveButton
                                                            type="button"
                                                            className="transparent-btn"
                                                            data-testid="edit-close"
                                                            title={text('btnCancel')}
                                                            onClick={() => {
                                                                setEditableRow(null)
                                                            }}
                                                        >
                                                            <CloseIcon color={theme.brandColors.CA_Coral} />
                                                        </SEditSaveButton>

                                                        <SEditSaveButton
                                                            type="button"
                                                            onClick={() => {
                                                                doEditCurrentLimitScheme(item?.id)
                                                            }}
                                                            title={text('btnEdit')}
                                                            data-testid="edit-save"
                                                            disabled={isSubmitting || !timeData.startHours}
                                                        >
                                                            <CheckMarkIcon />
                                                        </SEditSaveButton>
                                                    </span>
                                                ) : (
                                                    <span style={{ float: 'right' }} className="flex gap-2">
                                                        <DropdownMenu data-testid="menu-toggle-chargepoint">
                                                            <DropdownMenuItem
                                                                onClick={() => handleEditToggle(item?.id)}
                                                                text={text('btnEdit')}
                                                                icon={
                                                                    <EditIcon
                                                                        size={14}
                                                                        color={theme.brandColors.CA_Black}
                                                                    />
                                                                }
                                                                key="btnEditSchedule"
                                                                disabled={group?.active || openTimePicker}
                                                                data-testid="btn-delete-schedule"
                                                                disabledReason={
                                                                    group?.active ? text('msgLbActive') : undefined
                                                                }
                                                            />
                                                            <DropdownMenuItem
                                                                onClick={() => handleRemoveCurrent(item?.id)}
                                                                text={text('btnRemove')}
                                                                icon={<TrashIcon size={14} />}
                                                                key="btnRemoveSchedule"
                                                                disabled={group?.active}
                                                                data-testid="btn-remove-schedule"
                                                                disabledReason={
                                                                    group?.active ? text('msgLbActive') : undefined
                                                                }
                                                            />
                                                        </DropdownMenu>
                                                    </span>
                                                ))}
                                        </TableBodyCell>
                                    </TableRow>
                                ))}
                            {showGreenInterval && greenChargingStatus.greenOnlyInterval && (
                                <TableRow>
                                    <TableBodyCell data-testid="gc-time">
                                        {getGreenChargingInterval(greenChargingStatus.greenOnlyInterval, false)}
                                    </TableBodyCell>
                                    <TableBodyCell data-testid="gc-current">0</TableBodyCell>
                                </TableRow>
                            )}

                            {showGreenInterval &&
                                greenChargingStatus.greenOnlyInterval &&
                                !isFullDayOfCharging(greenChargingStatus.greenOnlyInterval) && (
                                    <TableRow key={currentLimitScheme?.id}>
                                        <TableBodyCell data-testid="gc-time-reverse">
                                            {getGreenChargingInterval(greenChargingStatus.greenOnlyInterval, true)}
                                        </TableBodyCell>
                                        <TableBodyCell data-testid="gc-current-reverse">
                                            {group.supplyMaxCurrent}
                                        </TableBodyCell>
                                    </TableRow>
                                )}
                            {openTimePicker && (
                                <TableRow className="input-row">
                                    <TableBodyCell>
                                        <Input
                                            id="time"
                                            type="time"
                                            onChange={(event) => {
                                                const time = event.target.value

                                                const [hours, minutes] = time && time.split(':')

                                                setTimeData({
                                                    ...timeData,
                                                    startMinutes: Number(minutes),
                                                    startHours: Number(hours),
                                                })
                                            }}
                                            data-testid="addTime"
                                            min="00:00"
                                            max="23:59"
                                        />
                                    </TableBodyCell>

                                    <TableBodyCell>
                                        <Input
                                            id="current"
                                            unit="A"
                                            {...register('current')}
                                            onChange={(e) =>
                                                setTimeData({
                                                    ...timeData,
                                                    current: e.target.value ? Number(e.target.value) : undefined,
                                                })
                                            }
                                            type="number"
                                            data-testid="input-current"
                                            min="0"
                                        ></Input>
                                    </TableBodyCell>

                                    <TableBodyCell>
                                        <span style={{ float: 'right' }} className="flex gap-2 mt-3">
                                            <SEditSaveButton
                                                className="transparent-btn"
                                                data-testid="edit-close"
                                                onClick={() => {
                                                    setOpenTimePicker(false)
                                                }}
                                                title={text('btnCancel')}
                                            >
                                                <CloseIcon color={theme.brandColors.CA_Coral} />
                                            </SEditSaveButton>

                                            <SEditSaveButton
                                                type="submit"
                                                onClick={() => {
                                                    addCurrent()
                                                }}
                                                title={text('btnAdd')}
                                                data-testid="add-save"
                                                disabled={isSubmitting || !timeData?.startHours}
                                            >
                                                <CheckMarkIcon />
                                            </SEditSaveButton>
                                        </span>
                                    </TableBodyCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </form>
            )}

            {showScheduleButton && (
                <div className="flex justify-content-end mt-3">
                    <Button
                        type="button"
                        data-testid="add-schedule-button"
                        disabled={disabledScheduleButton}
                        icon="pi pi-plus"
                        label={text('btnAddLoadBalancerScheduleSlot')}
                        onClick={() => setOpenTimePicker(true)}
                        tooltip={(() => {
                            if (group?.active) {
                                return text('msgLbActive')
                            } else if (greenChargingStatus?.greenChargingOn && group.active) {
                                return text('msgChargerIsInGreenChargingMode')
                            } else {
                                return text('btnAddLoadBalancerScheduleSlot')
                            }
                        })()}
                        tooltipOptions={{ position: 'top', showOnDisabled: true }}
                    ></Button>
                </div>
            )}
        </Panel>
    )
})
